import { useEffect, useState, useMemo, useCallback, useReducer, useRef } from 'react';
import { useField } from 'formik';
import {
	CurveConfig,
	CurveMethod,
	mapCurveMethodResponseToCurveMethod,
	WeibullDeclineReason,
	WeibullFormulation,
} from '@Types';
import { CurvePoint, CurveMethodData, WeibullParams, generateAvailableDataSeries, isFlatCurve } from './utils';
import {
	inferByIndustryStandardCurve,
	inferFailureDefinition,
	getCurveConfig,
	InferByFailureDefinitionResponse,
} from '@Services/deteriorationModels.services';
import { AxiosResponse } from 'axios';
import { error, useGlobalization } from '@innovyze/stylovyze';
import { useDispatch } from 'react-redux';
import { selectCurveConfig } from '@Selectors/deteriorationModels.selector';
import { baseChartOptions } from '@Components/deteriorationModel/utils/chartDefaults';

// Unified curve data structure
interface CurveData {
	points: CurvePoint[];
	loading: boolean;
}

// State for all curve types
interface CurveState {
	selectedCurveMethod: CurveMethod;
	customCurve: CurveData;
	industryCurve: CurveData & {
		selectedKey: string | null;
		allData: Record<string, CurvePoint[]>;
	};
	failureDefinitionCurve: {
		loading: boolean;
		// False until a fetch has completed successfully. Distinguishes "not fetched yet" from
		// "fetched, and this group has no trained fits at all".
		resolved: boolean;
		allData: Partial<Record<WeibullFormulation, CurvePoint[]>>;
		// Coefficients each curve was fitted with, keyed the same way as `allData`. Kept so the UI
		// can show what a curve was drawn from — a flat curve is only explicable from its params.
		allParams: Partial<Record<WeibullFormulation, WeibullParams>>;
		// Why a formulation is absent from `allData`, keyed the same way. Empty for groups trained
		// before I360A-2882, so a missing entry means "unavailable, reason unspecified".
		declines: Partial<Record<WeibullFormulation, WeibullDeclineReason>>;
	};
	copiedCurve: CurveData & {
		selectedKey: string | null;
		name: string | null;
		// Per-formulation fits, populated only when the copy source is a weibull curve. Empty for
		// custom/industry sources, whose single curve lives in `points`.
		allData: Partial<Record<WeibullFormulation, CurvePoint[]>>;
		// Why a formulation is absent from `allData`, when the source group's fits recorded one. A
		// copied curve is a real fit of a real group, so it can explain its own fallback rather than
		// falling back to the generic wording the way an unexplained decline has to.
		declines: Partial<Record<WeibullFormulation, WeibullDeclineReason>>;
	};
	oneExpectedUsefulLife: number | null;
	weibullFormulation: WeibullFormulation;
	curveConfig: {
		inProcess: boolean;
		data: CurveConfig | null;
	};
}

// Action types for reducer
type CurveAction =
	| { type: 'CLEAR_ALL'; payload?: { weibullFormulation?: WeibullFormulation } }
	| { type: 'SET_SELECTED_METHOD'; payload: CurveMethod }
	| { type: 'SET_CUSTOM_CURVE_POINTS'; payload: CurvePoint[] }
	| { type: 'SET_INDUSTRY_CURVE_LOADING'; payload: boolean }
	| { type: 'SET_INDUSTRY_CURVE_DATA'; payload: { allData: Record<string, CurvePoint[]> } }
	| { type: 'SET_INDUSTRY_CURVE_SELECTED'; payload: { key: string; points: CurvePoint[] } }
	| { type: 'SET_INDUSTRY_CURVE_POINTS'; payload: CurvePoint[] }
	| { type: 'SET_FAILURE_DEFINITION_LOADING'; payload: boolean }
	| {
			type: 'SET_FAILURE_DEFINITION_DATA';
			payload: {
				allData: Partial<Record<WeibullFormulation, CurvePoint[]>>;
				allParams: Partial<Record<WeibullFormulation, WeibullParams>>;
				declines: Partial<Record<WeibullFormulation, WeibullDeclineReason>>;
				resolved: boolean;
			};
	  }
	| { type: 'SET_COPIED_CURVE_LOADING'; payload: boolean }
	| {
			type: 'SET_COPIED_CURVE_DATA';
			payload: {
				key: string;
				points: CurvePoint[];
				name: string | null;
				allData?: Partial<Record<WeibullFormulation, CurvePoint[]>>;
				declines?: Partial<Record<WeibullFormulation, WeibullDeclineReason>>;
			};
	  }
	| { type: 'SET_COPIED_CURVE_SELECTED'; payload: string | null }
	| { type: 'SET_ONE_EXPECTED_LIFE'; payload: number | null }
	| { type: 'SET_WEIBULL_FORMULATION'; payload: WeibullFormulation }
	| { type: 'SET_CURVE_CONFIG'; payload: { inProcess: boolean; data: CurveConfig | null } }
	| { type: 'RESET_TO_INITIAL_CONFIG'; payload: CurveConfig };

// Initial state
export const initialCurveState: CurveState = {
	selectedCurveMethod: CurveMethod.UNDEFINED,
	customCurve: {
		points: [],
		loading: false,
	},
	industryCurve: {
		points: [],
		loading: false,
		selectedKey: null,
		allData: {},
	},
	failureDefinitionCurve: {
		loading: false,
		resolved: false,
		allData: {},
		allParams: {},
		declines: {},
	},
	copiedCurve: {
		points: [],
		loading: false,
		selectedKey: null,
		name: null,
		allData: {},
		declines: {},
	},
	oneExpectedUsefulLife: null,
	weibullFormulation: WeibullFormulation.TWO_PARAMETER,
	curveConfig: {
		inProcess: false,
		data: null,
	},
};

// Reducer function
export function curveReducer(state: CurveState, action: CurveAction): CurveState {
	switch (action.type) {
		case 'CLEAR_ALL':
			return {
				...initialCurveState,
				// Keep inheriting the model-level formulation; falling back to the hardcoded default
				// would silently revert a 3P model's group to 2P on cancel.
				weibullFormulation: action.payload?.weibullFormulation ?? initialCurveState.weibullFormulation,
				curveConfig: {
					inProcess: false,
					data: null,
				},
			};

		case 'SET_SELECTED_METHOD':
			return { ...state, selectedCurveMethod: action.payload };

		case 'SET_CUSTOM_CURVE_POINTS':
			return {
				...state,
				customCurve: {
					...state.customCurve,
					points: action.payload,
				},
			};

		case 'SET_INDUSTRY_CURVE_LOADING':
			return {
				...state,
				industryCurve: {
					...state.industryCurve,
					loading: action.payload,
				},
			};

		case 'SET_INDUSTRY_CURVE_DATA':
			return {
				...state,
				industryCurve: {
					...state.industryCurve,
					allData: action.payload.allData,
				},
			};

		case 'SET_INDUSTRY_CURVE_SELECTED':
			return {
				...state,
				industryCurve: {
					...state.industryCurve,
					selectedKey: action.payload.key,
					points: action.payload.points,
				},
			};

		case 'SET_INDUSTRY_CURVE_POINTS':
			return {
				...state,
				industryCurve: {
					...state.industryCurve,
					points: action.payload,
				},
			};

		case 'SET_FAILURE_DEFINITION_LOADING':
			return {
				...state,
				failureDefinitionCurve: {
					...state.failureDefinitionCurve,
					loading: action.payload,
				},
			};

		case 'SET_FAILURE_DEFINITION_DATA':
			return {
				...state,
				failureDefinitionCurve: {
					...state.failureDefinitionCurve,
					allData: action.payload.allData,
					allParams: action.payload.allParams,
					declines: action.payload.declines,
					resolved: action.payload.resolved,
				},
			};

		case 'SET_COPIED_CURVE_LOADING':
			return {
				...state,
				copiedCurve: {
					...state.copiedCurve,
					loading: action.payload,
				},
			};

		case 'SET_COPIED_CURVE_DATA':
			return {
				...state,
				copiedCurve: {
					...state.copiedCurve,
					selectedKey: action.payload.key,
					name: action.payload.name,
					points: action.payload.points,
					allData: action.payload.allData ?? {},
					declines: action.payload.declines ?? {},
				},
			};

		case 'SET_COPIED_CURVE_SELECTED':
			// Only a genuine change of source clears anything. The picker dispatches on every click,
			// including on the group already selected -- its options are freshly mapped object literals
			// while `value` is found in a different array, so MUI's identity check never matches. The
			// fetch effect keys on the selected key, so clearing on a same-key dispatch would empty the
			// preview with nothing left to trigger a refetch.
			if (action.payload === state.copiedCurve.selectedKey) {
				return state;
			}
			return {
				...state,
				copiedCurve: {
					...state.copiedCurve,
					selectedKey: action.payload,
					// Everything else describes the outgoing source, and two sequential round-trips separate
					// this from the new one arriving. Keeping any of it attributes one group's curve, name or
					// decline reason to another -- `points` and `name` matter as much as the fits here, since a
					// custom or industry source stores its curve in `points` with `allData` left empty.
					points: [],
					name: null,
					allData: {},
					declines: {},
				},
			};

		case 'SET_ONE_EXPECTED_LIFE':
			return { ...state, oneExpectedUsefulLife: action.payload };

		case 'SET_WEIBULL_FORMULATION':
			return { ...state, weibullFormulation: action.payload };

		case 'SET_CURVE_CONFIG':
			return {
				...state,
				curveConfig: {
					inProcess: action.payload.inProcess,
					data: action.payload.data,
				},
			};

		case 'RESET_TO_INITIAL_CONFIG': {
			const config = action.payload;
			const newState: CurveState = {
				...state,
				selectedCurveMethod: config.selectedCurveMethod,
				customCurve: {
					...state.customCurve,
					points: [],
				},
				industryCurve: {
					...state.industryCurve,
					selectedKey: null,
				},
				copiedCurve: {
					...state.copiedCurve,
					selectedKey: null,
					name: null,
					allData: {},
					declines: {},
				},
				oneExpectedUsefulLife: null,
				weibullFormulation: config.weibullFormulation ?? WeibullFormulation.TWO_PARAMETER,
			};

			if (config.selectedCurveMethod === CurveMethod.CUSTOM_CURVE) {
				newState.customCurve = {
					...newState.customCurve,
					points: config.data as CurvePoint[],
				};
			}
			if (config.selectedCurveMethod === CurveMethod.INDUSTRY_CURVE) {
				newState.industryCurve = {
					...newState.industryCurve,
					selectedKey: config.reference,
				};
			}
			if (config.selectedCurveMethod === CurveMethod.ONE_EXPECTED_LIFE) {
				newState.oneExpectedUsefulLife = config.data as number;
			}
			if (config.selectedCurveMethod === CurveMethod.COPIED_CURVE) {
				newState.copiedCurve = {
					...newState.copiedCurve,
					selectedKey: config.reference,
				};
			}

			return newState;
		}

		default:
			return state;
	}
}

// Custom hook for industry curve data fetching
const useIndustryCurveData = (
	selectedMethod: CurveMethod,
	industryCurves: { curves: { key: string; displayName: string }[]; isLoading: boolean },
	allData: Record<string, CurvePoint[]>,
	dispatch: React.Dispatch<CurveAction>,
) => {
	const globalDispatch = useDispatch();
	const { t } = useGlobalization();

	useEffect(() => {
		if (
			selectedMethod === CurveMethod.INDUSTRY_CURVE &&
			!industryCurves.isLoading &&
			Object.keys(allData).length === 0
		) {
			const fetchData = async () => {
				dispatch({ type: 'SET_INDUSTRY_CURVE_LOADING', payload: true });
				try {
					const allCurveKeys = industryCurves.curves.map(curve => curve.key);
					const response = (await inferByIndustryStandardCurve({
						curveKeys: allCurveKeys,
					})) as AxiosResponse<{ data: Record<string, { age: number; survival_probability: number }[]> }>;

					const allCurveData: Record<string, CurvePoint[]> = {};
					Object.entries(response.data.data).forEach(([curveKey, curveData]) => {
						if (curveData) {
							allCurveData[curveKey] = curveData.map(point => ({
								age: point.age,
								survivalProbability: point.survival_probability * 100,
							}));
						}
					});
					dispatch({ type: 'SET_INDUSTRY_CURVE_DATA', payload: { allData: allCurveData } });
				} catch (e) {
					console.error('Failed to fetch industry standard curve points', e);
					globalDispatch(error(t('Failed to fetch industry standard curve points')));
					dispatch({ type: 'SET_INDUSTRY_CURVE_DATA', payload: { allData: {} } });
				} finally {
					dispatch({ type: 'SET_INDUSTRY_CURVE_LOADING', payload: false });
				}
			};

			fetchData();
		}
	}, [selectedMethod]);
};

/**
 * Splits an infer response into per-formulation curve points. The endpoint returns a key only for
 * formulations the group was genuinely fitted with, so a missing key means "no fit of that kind".
 */
export const mapFailureDefinitionResponse = (
	response: InferByFailureDefinitionResponse | undefined,
): {
	allData: Partial<Record<WeibullFormulation, CurvePoint[]>>;
	allParams: Partial<Record<WeibullFormulation, WeibullParams>>;
	declines: Partial<Record<WeibullFormulation, WeibullDeclineReason>>;
	resolved: boolean;
} => {
	const allData: Partial<Record<WeibullFormulation, CurvePoint[]>> = {};
	const allParams: Partial<Record<WeibullFormulation, WeibullParams>> = {};

	Object.entries(response?.data ?? {}).forEach(([formulation, result]) => {
		// Skip keys with no points. Storing an empty array would make the key-presence checks below
		// report a successful fit while the chart drew nothing.
		if (!result?.inference_results?.length) return;
		allData[formulation as WeibullFormulation] = result.inference_results.map(point => ({
			age: point.age,
			survivalProbability: point.survival_probability * 100,
		}));
		// Params are optional on the contract, so leave the key absent rather than storing `{}` —
		// the UI distinguishes "no coefficients reported" from "coefficients are all zero".
		if (result.params) {
			allParams[formulation as WeibullFormulation] = result.params;
		}
	});

	return { allData, allParams, declines: response?.declines ?? {}, resolved: true };
};

/**
 * A formulation counts as missing only when the group has some fits but not this one. An empty map
 * means either no fits at all or a source with no per-formulation data — a different signal, which
 * `noCurveFitted` covers separately.
 */
const isFormulationMissing = (
	allData: Partial<Record<WeibullFormulation, CurvePoint[]>>,
	formulation: WeibullFormulation,
): boolean => Object.keys(allData).length > 0 && allData[formulation] === undefined;

// Custom hook for failure definition curve data fetching
const useFailureDefinitionCurveData = (
	selectedMethod: CurveMethod,
	modelId: string,
	curveConfigId: string,
	dispatch: React.Dispatch<CurveAction>,
) => {
	const globalDispatch = useDispatch();
	const { t } = useGlobalization();
	// The response covers every formulation, so one fetch per group is enough; switching 2P/3P
	// is a local selection. Tracked by ref so re-selecting the method does not refetch.
	const fetchedGroupRef = useRef<string | null>(null);

	useEffect(() => {
		if (selectedMethod === CurveMethod.FAILURE_DEFINITION && fetchedGroupRef.current !== curveConfigId) {
			fetchedGroupRef.current = curveConfigId;
			const fetchData = async () => {
				dispatch({ type: 'SET_FAILURE_DEFINITION_LOADING', payload: true });
				try {
					const response = await inferFailureDefinition(modelId, curveConfigId);

					dispatch({
						type: 'SET_FAILURE_DEFINITION_DATA',
						payload: mapFailureDefinitionResponse(response.data),
					});
				} catch (e) {
					// Release the group so re-selecting the method retries; leaving it marked as fetched
					// would strand the chart empty for the rest of the session with no way to recover.
					fetchedGroupRef.current = null;
					console.error('Failed to fetch failure definition curve points', e);
					globalDispatch(error(t('Failed to fetch failure definition curve points')));
					dispatch({
						type: 'SET_FAILURE_DEFINITION_DATA',
						payload: { allData: {}, allParams: {}, declines: {}, resolved: false },
					});
				} finally {
					dispatch({ type: 'SET_FAILURE_DEFINITION_LOADING', payload: false });
				}
			};

			fetchData();
		}
	}, [selectedMethod, curveConfigId]);
};

// Custom hook for copied curve data fetching
const useCopiedCurveData = (
	modelId: string,
	selectedKey: string | null,
	selectedMethod: CurveMethod,
	dispatch: React.Dispatch<CurveAction>,
) => {
	const globalDispatch = useDispatch();
	const { t } = useGlobalization();
	// Sequence guard: the source group can be changed while a fetch is in flight, and without this
	// a slower earlier response could land last and chart the wrong group.
	const latestKeyRef = useRef<string | null>(null);

	useEffect(() => {
		if (selectedMethod === CurveMethod.COPIED_CURVE && selectedKey) {
			latestKeyRef.current = selectedKey;
			const fetchData = async () => {
				dispatch({ type: 'SET_COPIED_CURVE_LOADING', payload: true });
				try {
					const response = await getCurveConfig(modelId, selectedKey);
					const sourceConfig = response.data.data;
					const sourceMethod = mapCurveMethodResponseToCurveMethod(sourceConfig.selected_curve_method.method);

					let curvePoints: CurvePoint[] = [];
					// Only a weibull source has per-formulation fits; the selection happens locally.
					let allData: Partial<Record<WeibullFormulation, CurvePoint[]>> = {};
					let declines: Partial<Record<WeibullFormulation, WeibullDeclineReason>> = {};

					if (sourceMethod === CurveMethod.CUSTOM_CURVE) {
						if (Array.isArray(sourceConfig.selected_curve_method.custom_data)) {
							curvePoints = sourceConfig.selected_curve_method.custom_data.map(point => ({
								age: point.age,
								survivalProbability: point.survival_probability * 100,
							}));
						}
					} else if (
						sourceMethod === CurveMethod.INDUSTRY_CURVE &&
						sourceConfig.selected_curve_method.industry_curve_key
					) {
						const industryResponse = (await inferByIndustryStandardCurve({
							curveKeys: [sourceConfig.selected_curve_method.industry_curve_key],
						})) as AxiosResponse<{ data: Record<string, { age: number; survival_probability: number }[]> }>;

						const industryData = industryResponse.data.data[sourceConfig.selected_curve_method.industry_curve_key];
						if (industryData) {
							curvePoints = industryData.map(point => ({
								age: point.age,
								survivalProbability: point.survival_probability * 100,
							}));
						}
					} else if (sourceMethod === CurveMethod.FAILURE_DEFINITION) {
						const failureResponse = await inferFailureDefinition(modelId, selectedKey);
						// Take the reasons too: a copied curve is the source group's own fit, so it can say why
						// the formulation you picked is missing instead of falling back to the generic wording.
						({ allData, declines } = mapFailureDefinitionResponse(failureResponse.data));
					}

					if (latestKeyRef.current !== selectedKey) return;

					dispatch({
						type: 'SET_COPIED_CURVE_DATA',
						payload: {
							key: selectedKey,
							points: curvePoints,
							name: sourceConfig.asset_group_name,
							allData,
							declines,
						},
					});
				} catch (e) {
					if (latestKeyRef.current !== selectedKey) return;
					console.error('Failed to fetch copied curve data', e);
					globalDispatch(error(t('Failed to fetch copied curve data')));
					dispatch({ type: 'SET_COPIED_CURVE_DATA', payload: { key: selectedKey, points: [], name: null } });
				} finally {
					if (latestKeyRef.current === selectedKey) {
						dispatch({ type: 'SET_COPIED_CURVE_LOADING', payload: false });
					}
				}
			};

			fetchData();
		}
	}, [selectedMethod, selectedKey, modelId]);
};

// Main curve configuration hook
export const useCurveConfig = (
	modelId: string,
	curveConfigId: string,
	industryStandardCurves: { curves: { key: string; displayName: string }[]; isLoading: boolean },
	weibullFormulation: WeibullFormulation = WeibullFormulation.TWO_PARAMETER,
) => {
	const [, meta, helpers] = useField(curveConfigId);
	const { value } = meta;
	const { setValue } = helpers;

	const [state, localDispatch] = useReducer(curveReducer, initialCurveState, initial => ({
		...initial,
		weibullFormulation,
	}));
	const selectorCurveConfig = selectCurveConfig(value.id);

	// Fetch initial curve configuration
	useEffect(() => {
		if (isCancellingRef.current) return;

		// Check if we have local changes stored in formik state
		if (value.updatedCurveMethodData && value.selectedCurveMethod !== CurveMethod.UNDEFINED) {
			// Use local changes
			const curveConfig: CurveConfig = {
				curveConfigId: value.id,
				selectedCurveMethod: value.updatedCurveMethodData.selectedCurveMethod,
				reference: value.updatedCurveMethodData.industryCurve || value.updatedCurveMethodData.copiedCurve || null,
				data: value.updatedCurveMethodData.customCurve || value.updatedCurveMethodData.oneExpectedLife || null,
				weibullFormulation: value.updatedCurveMethodData.weibullFormulation,
			};
			localDispatch({ type: 'SET_CURVE_CONFIG', payload: { inProcess: false, data: curveConfig } });
			return;
		}

		// No local changes, use selector data
		if (selectorCurveConfig) {
			localDispatch({ type: 'SET_CURVE_CONFIG', payload: { inProcess: false, data: selectorCurveConfig } });
		} else {
			localDispatch({ type: 'SET_CURVE_CONFIG', payload: { inProcess: false, data: null } });
		}
	}, [value.id, value.updatedCurveMethodData, value.selectedCurveMethod, selectorCurveConfig]);

	// Initialize state from loaded config or local changes (only once when data is first loaded)
	useEffect(() => {
		if (isCancellingRef.current) return;

		if (state.curveConfig.data && state.selectedCurveMethod === CurveMethod.UNDEFINED) {
			localDispatch({ type: 'SET_SELECTED_METHOD', payload: value.selectedCurveMethod });

			// If we have local changes, initialize from those instead of the config
			if (value.updatedCurveMethodData) {
				// Set the selected method from local changes
				localDispatch({ type: 'SET_SELECTED_METHOD', payload: value.updatedCurveMethodData.selectedCurveMethod });

				// Initialize state based on the local changes
				if (
					value.updatedCurveMethodData.selectedCurveMethod === CurveMethod.CUSTOM_CURVE &&
					value.updatedCurveMethodData.customCurve
				) {
					localDispatch({ type: 'SET_CUSTOM_CURVE_POINTS', payload: value.updatedCurveMethodData.customCurve });
				}
				if (
					value.updatedCurveMethodData.selectedCurveMethod === CurveMethod.INDUSTRY_CURVE &&
					value.updatedCurveMethodData.industryCurve
				) {
					localDispatch({
						type: 'SET_INDUSTRY_CURVE_SELECTED',
						payload: { key: value.updatedCurveMethodData.industryCurve, points: [] },
					});
				}
				if (
					value.updatedCurveMethodData.selectedCurveMethod === CurveMethod.ONE_EXPECTED_LIFE &&
					value.updatedCurveMethodData.oneExpectedLife !== undefined
				) {
					localDispatch({ type: 'SET_ONE_EXPECTED_LIFE', payload: value.updatedCurveMethodData.oneExpectedLife });
				}
				if (
					value.updatedCurveMethodData.selectedCurveMethod === CurveMethod.COPIED_CURVE &&
					value.updatedCurveMethodData.copiedCurve
				) {
					localDispatch({ type: 'SET_COPIED_CURVE_SELECTED', payload: value.updatedCurveMethodData.copiedCurve });
				}
				if (value.updatedCurveMethodData.weibullFormulation !== undefined) {
					localDispatch({
						type: 'SET_WEIBULL_FORMULATION',
						payload: value.updatedCurveMethodData.weibullFormulation,
					});
				}
			} else {
				// No local changes, use the loaded config.
				// `weibullFormulation` (the model-level prop) is read but deliberately omitted from the
				// dep array: the whole effect is gated on selectedCurveMethod === UNDEFINED above, so it
				// only ever runs at init and cannot observe a later change to the prop.
				localDispatch({
					type: 'RESET_TO_INITIAL_CONFIG',
					payload: {
						...state.curveConfig.data,
						weibullFormulation: state.curveConfig.data.weibullFormulation ?? weibullFormulation,
					},
				});
			}
		}
	}, [state.curveConfig.data, value.selectedCurveMethod, value.updatedCurveMethodData, state.selectedCurveMethod]);

	// Custom hooks for data fetching
	useIndustryCurveData(state.selectedCurveMethod, industryStandardCurves, state.industryCurve.allData, localDispatch);
	useFailureDefinitionCurveData(state.selectedCurveMethod, modelId, value.id, localDispatch);
	useCopiedCurveData(modelId, state.copiedCurve.selectedKey, state.selectedCurveMethod, localDispatch);

	// Update industry curve points when selection changes
	useEffect(() => {
		if (state.selectedCurveMethod === CurveMethod.INDUSTRY_CURVE && state.industryCurve.selectedKey) {
			const selectedCurveData = state.industryCurve.allData[state.industryCurve.selectedKey];
			if (selectedCurveData) {
				localDispatch({
					type: 'SET_INDUSTRY_CURVE_SELECTED',
					payload: { key: state.industryCurve.selectedKey, points: selectedCurveData },
				});
			} else {
				localDispatch({ type: 'SET_INDUSTRY_CURVE_POINTS', payload: [] });
			}
		}
	}, [
		state.selectedCurveMethod,
		state.industryCurve.selectedKey,
		state.industryCurve.allData,
		industryStandardCurves.curves,
	]);

	// Actions
	const setSelectedCurveMethod = useCallback((method: CurveMethod) => {
		localDispatch({ type: 'SET_SELECTED_METHOD', payload: method });
	}, []);

	const setCustomCurvePoints = useCallback(
		(points: React.SetStateAction<CurvePoint[]>) => {
			const newPoints = typeof points === 'function' ? points(state.customCurve.points) : points;
			localDispatch({ type: 'SET_CUSTOM_CURVE_POINTS', payload: newPoints });
		},
		[state.customCurve.points],
	);

	const setIndustryStandardCurve = useCallback(
		(key: string) => {
			localDispatch({
				type: 'SET_INDUSTRY_CURVE_SELECTED',
				payload: { key, points: state.industryCurve.allData[key] || [] },
			});
		},
		[state.industryCurve.allData],
	);

	const setOneExpectedUsefulLife = useCallback((value: number | null) => {
		localDispatch({ type: 'SET_ONE_EXPECTED_LIFE', payload: value });
	}, []);

	const setCopyCurve = useCallback((key: string | null) => {
		localDispatch({ type: 'SET_COPIED_CURVE_SELECTED', payload: key });
	}, []);

	const setWeibullFormulation = useCallback((formulation: WeibullFormulation) => {
		localDispatch({ type: 'SET_WEIBULL_FORMULATION', payload: formulation });
	}, []);

	const setCurveMethodData = useCallback(() => {
		const updatedCurveMethodData: CurveMethodData = {
			selectedCurveMethod: state.selectedCurveMethod,
			industryCurve: state.industryCurve.selectedKey,
			customCurve: state.customCurve.points,
			oneExpectedLife: state.oneExpectedUsefulLife,
			copiedCurve: state.copiedCurve.selectedKey,
			weibullFormulation: state.weibullFormulation,
		};

		setValue({
			...value,
			selectedCurveMethod: state.selectedCurveMethod,
			updatedCurveMethodData,
		});
	}, [setValue, value, state]);

	const clearLocalChanges = useCallback(() => {
		setValue({
			...value,
			updatedCurveMethodData: null,
		});
	}, [setValue, value]);

	const isInitializedRef = useRef(false);
	const isCancellingRef = useRef(false);

	const valueRef = useRef(value);
	const setValueRef = useRef(setValue);
	valueRef.current = value;
	setValueRef.current = setValue;

	useEffect(() => {
		if (isCancellingRef.current && value.updatedCurveMethodData === null) {
			isCancellingRef.current = false;
		}
	}, [value.updatedCurveMethodData]);

	const cancelChanges = useCallback(() => {
		isCancellingRef.current = true;

		const originalMethod = selectorCurveConfig?.selectedCurveMethod ?? CurveMethod.UNDEFINED;

		setValue({
			...value,
			selectedCurveMethod: originalMethod,
			updatedCurveMethodData: null,
		});

		if (selectorCurveConfig) {
			localDispatch({
				type: 'RESET_TO_INITIAL_CONFIG',
				payload: {
					...selectorCurveConfig,
					weibullFormulation: selectorCurveConfig.weibullFormulation ?? weibullFormulation,
				},
			});
		} else {
			localDispatch({ type: 'CLEAR_ALL', payload: { weibullFormulation } });
		}

		isInitializedRef.current = false;
	}, [setValue, value, selectorCurveConfig, weibullFormulation]);

	useEffect(() => {
		if (!isInitializedRef.current) {
			if (state.curveConfig.data && state.selectedCurveMethod !== CurveMethod.UNDEFINED) {
				isInitializedRef.current = true;
			}
			return;
		}

		const originalConfig = selectorCurveConfig;
		if (!originalConfig) return;

		let hasChanges = false;

		if (state.selectedCurveMethod !== originalConfig.selectedCurveMethod) {
			hasChanges = true;
		} else {
			switch (state.selectedCurveMethod) {
				case CurveMethod.INDUSTRY_CURVE:
					hasChanges = state.industryCurve.selectedKey !== originalConfig.reference;
					break;
				case CurveMethod.COPIED_CURVE:
					hasChanges = state.copiedCurve.selectedKey !== originalConfig.reference;
					break;
				case CurveMethod.ONE_EXPECTED_LIFE:
					hasChanges = state.oneExpectedUsefulLife !== originalConfig.data;
					break;
				case CurveMethod.CUSTOM_CURVE: {
					const originalPoints = originalConfig.data as CurvePoint[] | null;
					if (!originalPoints) {
						hasChanges = state.customCurve.points.length > 0;
					} else if (state.customCurve.points.length !== originalPoints.length) {
						hasChanges = true;
					} else {
						hasChanges = state.customCurve.points.some(
							(point, index) =>
								point.age !== originalPoints[index]?.age ||
								point.survivalProbability !== originalPoints[index]?.survivalProbability,
						);
					}
					break;
				}
			}
		}

		// Weibull formulation is only editable for failure-definition/custom-curve groups, but
		// track it here regardless of method so a formulation change alone marks the row dirty.
		if (
			!hasChanges &&
			(state.selectedCurveMethod === CurveMethod.FAILURE_DEFINITION ||
				state.selectedCurveMethod === CurveMethod.CUSTOM_CURVE)
		) {
			hasChanges = state.weibullFormulation !== (originalConfig.weibullFormulation ?? WeibullFormulation.TWO_PARAMETER);
		}

		const currentValue = valueRef.current;
		const currentSetValue = setValueRef.current;

		if (hasChanges) {
			const updatedCurveMethodData: CurveMethodData = {
				selectedCurveMethod: state.selectedCurveMethod,
				industryCurve: state.industryCurve.selectedKey,
				customCurve: state.customCurve.points,
				oneExpectedLife: state.oneExpectedUsefulLife,
				copiedCurve: state.copiedCurve.selectedKey,
				weibullFormulation: state.weibullFormulation,
			};
			currentSetValue({
				...currentValue,
				selectedCurveMethod: state.selectedCurveMethod,
				updatedCurveMethodData,
			});
		} else {
			if (currentValue.updatedCurveMethodData !== null) {
				currentSetValue({
					...currentValue,
					updatedCurveMethodData: null,
				});
			}
		}
	}, [
		state.selectedCurveMethod,
		state.industryCurve.selectedKey,
		state.copiedCurve.selectedKey,
		state.oneExpectedUsefulLife,
		state.customCurve.points,
		state.weibullFormulation,
		selectorCurveConfig,
	]);

	// Fall back to the 2-parameter curve when the group has no fit for the selected formulation,
	// so the chart still shows something; `selectedFormulationUnavailable` explains why.
	const failureDefinitionCurvePoints = useMemo(
		() =>
			state.failureDefinitionCurve.allData[state.weibullFormulation] ??
			state.failureDefinitionCurve.allData[WeibullFormulation.TWO_PARAMETER] ??
			[],
		[state.failureDefinitionCurve.allData, state.weibullFormulation],
	);

	// Same single-fetch-plus-local-selection as the failure-definition path. A source with no
	// per-formulation fits (custom/industry) keeps its already-resolved single curve.
	const copiedCurvePoints = useMemo(() => {
		if (Object.keys(state.copiedCurve.allData).length === 0) return state.copiedCurve.points;
		return (
			state.copiedCurve.allData[state.weibullFormulation] ??
			state.copiedCurve.allData[WeibullFormulation.TWO_PARAMETER] ??
			[]
		);
	}, [state.copiedCurve.allData, state.copiedCurve.points, state.weibullFormulation]);

	// Coefficients for the curve actually on the chart. Follows the same fallback chain as
	// `failureDefinitionCurvePoints` — showing the selected formulation's params next to a curve
	// drawn from the 2-parameter fit would misattribute the curve. Cleared for other methods: the
	// fetched params outlive a method switch, and an industry or custom curve is not fitted here.
	const selectedFormulationParams = useMemo(() => {
		if (state.selectedCurveMethod !== CurveMethod.FAILURE_DEFINITION) return undefined;
		return (
			state.failureDefinitionCurve.allParams[state.weibullFormulation] ??
			state.failureDefinitionCurve.allParams[WeibullFormulation.TWO_PARAMETER]
		);
	}, [state.selectedCurveMethod, state.failureDefinitionCurve.allParams, state.weibullFormulation]);

	// The fit on the chart is present and reported as successful, but its curve never moves, so it
	// cannot be modelled with. Only weibull-backed methods are checked: a custom curve is allowed
	// to be flat because a user drew it that way.
	const selectedFitDegenerate = useMemo(() => {
		if (state.selectedCurveMethod === CurveMethod.FAILURE_DEFINITION) {
			return isFlatCurve(failureDefinitionCurvePoints);
		}
		if (state.selectedCurveMethod === CurveMethod.COPIED_CURVE) {
			// An empty `allData` means the source was a custom or industry curve, not a fit.
			return Object.keys(state.copiedCurve.allData).length > 0 && isFlatCurve(copiedCurvePoints);
		}
		return false;
	}, [state.selectedCurveMethod, failureDefinitionCurvePoints, state.copiedCurve.allData, copiedCurvePoints]);

	// The group has too few failures to fit any curve at all. Terminal, not transient: a group
	// below the training threshold returns an empty payload every time.
	const noCurveFitted = useMemo(
		() =>
			state.selectedCurveMethod === CurveMethod.FAILURE_DEFINITION &&
			state.failureDefinitionCurve.resolved &&
			Object.keys(state.failureDefinitionCurve.allData).length === 0,
		[state.selectedCurveMethod, state.failureDefinitionCurve.resolved, state.failureDefinitionCurve.allData],
	);

	// The group has some fits, but not one for the selected formulation — the API omitted its key.
	// Applies to the copied-curve source too: it is a single known group, so its fit availability is
	// just as knowable as the group's own.
	const selectedFormulationUnavailable = useMemo(() => {
		if (state.selectedCurveMethod === CurveMethod.FAILURE_DEFINITION) {
			// `resolved` separates "not fetched yet" from "fetched, and nothing was fitted".
			if (!state.failureDefinitionCurve.resolved) return false;
			return isFormulationMissing(state.failureDefinitionCurve.allData, state.weibullFormulation);
		}
		if (state.selectedCurveMethod === CurveMethod.COPIED_CURVE) {
			return isFormulationMissing(state.copiedCurve.allData, state.weibullFormulation);
		}
		return false;
	}, [
		state.selectedCurveMethod,
		state.failureDefinitionCurve.resolved,
		state.failureDefinitionCurve.allData,
		state.copiedCurve.allData,
		state.weibullFormulation,
	]);

	// The reason the backend gave for declining the selected formulation, when it gave one.
	// Only meaningful alongside `selectedFormulationUnavailable`; undefined either because the
	// group predates I360A-2882 or because the formulation is present. Tracks the same two methods
	// as `selectedFormulationUnavailable` — a copied curve shows the banner too, and answering
	// "unavailable" there while refusing to say why would be the catch-all this ticket removes.
	const selectedFormulationDeclineReason = useMemo(() => {
		if (state.selectedCurveMethod === CurveMethod.FAILURE_DEFINITION) {
			return state.failureDefinitionCurve.declines[state.weibullFormulation];
		}
		if (state.selectedCurveMethod === CurveMethod.COPIED_CURVE) {
			return state.copiedCurve.declines[state.weibullFormulation];
		}
		return undefined;
	}, [
		state.selectedCurveMethod,
		state.failureDefinitionCurve.declines,
		state.copiedCurve.declines,
		state.weibullFormulation,
	]);

	return {
		// Config state
		curveConfig: state.curveConfig,
		originalSavedConfig: selectorCurveConfig,

		// Method selection
		selectedCurveMethod: state.selectedCurveMethod,
		setSelectedCurveMethod,

		// Custom curve
		customCurvePoints: state.customCurve.points,
		setCustomCurvePoints,

		// Industry curve
		industryStandardCurve: state.industryCurve.selectedKey,
		setIndustryStandardCurve,
		industryStandardCurvePoints: state.industryCurve.points,
		industryStandardCurveLoading: state.industryCurve.loading,

		// Failure definition curve — derived from the selected formulation, no refetch on toggle
		failureDefinitionCurvePoints,
		failureDefinitionCurveLoading: state.failureDefinitionCurve.loading,
		selectedFormulationUnavailable,
		selectedFormulationDeclineReason,
		selectedFormulationParams,
		selectedFitDegenerate,
		noCurveFitted,

		// Copied curve
		copyCurve: state.copiedCurve.selectedKey,
		copyCurveName: state.copiedCurve.name,
		setCopyCurve,
		copiedCurvePoints,
		copiedCurveLoading: state.copiedCurve.loading,

		// One expected life
		oneExpectedUsefulLife: state.oneExpectedUsefulLife,
		setOneExpectedUsefulLife,

		// Weibull formulation (per-group, editable draft — FD/custom-curve only)
		weibullFormulation: state.weibullFormulation,
		setWeibullFormulation,

		// Actions
		setCurveMethodData,
		clearLocalChanges,
		cancelChanges,
	};
};

export const useChartData = (curveState: ReturnType<typeof useCurveConfig>) => {
	const { t } = useGlobalization();
	const [selectedLines, setSelectedLines] = useState<string[]>([]);
	const [autoAddedCurves, setAutoAddedCurves] = useState<Set<string>>(new Set());

	const availableDataSeries = useMemo(
		() =>
			generateAvailableDataSeries(
				t,
				curveState.customCurvePoints,
				curveState.industryStandardCurvePoints,
				curveState.failureDefinitionCurvePoints,
				curveState.copiedCurvePoints,
			),
		[curveState],
	);

	// Auto-select curves that have data for the first time
	useEffect(() => {
		const curvesWithData = availableDataSeries.filter(series => series.data.length > 0);
		const newSelectedLines = [...selectedLines];
		const newAutoAddedCurves = new Set(autoAddedCurves);
		let hasChanges = false;

		// Add curves that have data but haven't been auto-added before
		curvesWithData.forEach(series => {
			if (!newAutoAddedCurves.has(series.id) && !newSelectedLines.includes(series.id)) {
				newSelectedLines.push(series.id);
				newAutoAddedCurves.add(series.id);
				hasChanges = true;
			}
		});

		// Always ensure the currently selected method is included if it has data
		const currentMethodSeries = availableDataSeries.find(series => series.id === curveState.selectedCurveMethod);
		if (
			currentMethodSeries &&
			currentMethodSeries.data.length > 0 &&
			!newSelectedLines.includes(curveState.selectedCurveMethod)
		) {
			newSelectedLines.push(curveState.selectedCurveMethod);
			newAutoAddedCurves.add(curveState.selectedCurveMethod);
			hasChanges = true;
		}

		if (hasChanges) {
			setSelectedLines(newSelectedLines);
			setAutoAddedCurves(newAutoAddedCurves);
		}
	}, [availableDataSeries, curveState.selectedCurveMethod, selectedLines, autoAddedCurves]);

	const chartOptions = useMemo(() => {
		const selectedSeries = availableDataSeries
			.filter(series => selectedLines.includes(series.id))
			.map(series => {
				const isSelectedMethod = series.id === curveState.selectedCurveMethod;
				return {
					type: 'spline' as const,
					name: series.name,
					data: [...series.data],
					color: series.color,
					// Highlight selected method
					opacity: isSelectedMethod ? 1 : 0.4,
					lineWidth: isSelectedMethod ? 3 : 2,
					marker: {
						enabled: isSelectedMethod,
						radius: isSelectedMethod ? 4 : 3,
					},
				};
			});

		return {
			...baseChartOptions,
			chart: {
				type: 'spline',
			},
			title: {
				text: '',
			},
			xAxis: {
				title: {
					text: 'Time (Years)',
				},
			},
			yAxis: {
				title: {
					text: 'Survival Probability (%)',
				},
			},
			legend: {
				enabled: selectedSeries.length > 1,
			},
			series: selectedSeries,
			tooltip: {
				shared: true,
				crosshairs: true,
			},
		};
	}, [selectedLines, availableDataSeries, curveState.selectedCurveMethod]);

	return {
		selectedLines,
		setSelectedLines,
		availableDataSeries,
		chartOptions,
	};
};
